import {
  getConnectedDisplayFields,
  isConnectedCharacterField,
  isConnectionSourceField,
} from "../../utils/fieldConnections";
import { getTemplateCategoryConfig } from "./utils/templateCategoryHelpers";
import { getSpecialForm } from "./specialFormsMap";
import {
  isTextConnectedField,
  getTextConnectionDisplayFields,
} from "../../utils/textFieldConnections";
import {
  isGenericFormField,
  normalizeGenericFields,
} from "../../utils/genericFieldSupport";
import { isPreviewGroupLabelRecord } from "../pages/admin/forms-management/editor/components/preview/previewFieldLabel";
import {
  applyFieldSettings,
  getFieldSettings,
} from "../../utils/fieldSettings";

export const CANVAS_AREA_FIELD_ID = "801fc1f4-945b-4ac7-8ca7-42f307178039";

export const isCanvasAreaField = (field) =>
  field?.field_id === CANVAS_AREA_FIELD_ID;

export const hasCanvasAreaMapping = (fields = []) =>
  Array.isArray(fields) && fields.some(isCanvasAreaField);

export const getCanvasDrawingState = (document = {}) => {
  const canvas = document?.canvas || {};

  return {
    lines: Array.isArray(canvas.lines) ? canvas.lines : [],
    shapes: Array.isArray(canvas.shapes) ? canvas.shapes : [],
    texts: Array.isArray(canvas.texts) ? canvas.texts : [],
  };
};

export const getPermitTypeData = (selectedRow, selectedPermitType) => {
  if (!selectedRow || !selectedPermitType)
    return {
      selected_documents: selectedRow?.selected_documents || [],
      documents: selectedRow?.documents || {},
    };

  // If it's the main permit type, return main data
  if (selectedPermitType === selectedRow.permit_type) {
    return {
      selected_documents: selectedRow.selected_documents || [],
      documents: selectedRow.documents || {},
    };
  }

  // If it's an additional permit, find it in additional_permits
  if (
    selectedRow.additional_permits &&
    selectedRow.additional_permits[selectedPermitType]
  ) {
    const additionalPermit = selectedRow.additional_permits[selectedPermitType];
    const permitData = Array.isArray(additionalPermit)
      ? additionalPermit[0]
      : additionalPermit;

    return {
      selected_documents: permitData?.selected_documents || [],
      documents: permitData?.documents || {},
    };
  }

  return {
    selected_documents: selectedRow?.selected_documents || [],
    documents: selectedRow?.documents || {},
  };
};

export const getCreatingDoc = (selectedRow, selectedPermitType, leftValue) => {
  const permitData = getPermitTypeData(selectedRow, selectedPermitType);
  const selectedDoc = permitData?.selected_documents?.[leftValue];
  const creatingDoc = selectedDoc?.form_id;

  const specialForm =
    getSpecialForm(selectedDoc) || getSpecialForm(selectedPermitType);
  if (specialForm) {
    return specialForm.key;
  }

  return creatingDoc;
};

export const getDocumentKey = (doc) => {
  const specialForm = getSpecialForm(doc);
  if (specialForm) return specialForm.key;

  return doc?.form_id || doc?.name || null;
};

export const getDocumentLabel = (doc) => doc?.label || doc?.name || "";

export const resolveInitialDocumentSize = ({
  selectedDocument = {},
  runtimeDocument = {},
  isNoc = false,
} = {}) => {
  if (isNoc) return "legal";

  return (
    selectedDocument?.size ||
    selectedDocument?.doc?.size ||
    runtimeDocument?.size ||
    runtimeDocument?.doc?.size ||
    "letter"
  );
};

const SPECIAL_RENDERER_FIELD_IDS = new Set(["pais", "pass", "generic_table"]);

export const isSpecialRendererFieldId = (fieldId) =>
  SPECIAL_RENDERER_FIELD_IDS.has(
    String(fieldId ?? "")
      .trim()
      .toLowerCase(),
  );

export const isPaisRendererField = (field) =>
  ["pais", "pass"].includes(
    String(field?.field_id ?? "")
      .trim()
      .toLowerCase(),
  );

export const getPaisDisplayFields = (fields = []) => {
  const seenFieldIds = new Set();

  return getTextConnectionDisplayFields(
    getConnectedDisplayFields(fields),
  ).filter((field) => {
    if (isConnectionSourceField(field) || isPaisRendererField(field)) {
      return false;
    }

    const fieldId = field?.field_id;

    // Fields without a stable field_id cannot be identified as duplicates.
    if (fieldId === undefined || fieldId === null || fieldId === "") {
      return true;
    }

    if (seenFieldIds.has(fieldId)) {
      return false;
    }

    seenFieldIds.add(fieldId);
    return true;
  });
};

export const buildPaisSubmissionFields = (
  fields = [],
  values = {},
  resolvedTextByWidgetName = new Map(),
) =>
  fields.map((field) => {
    const resolvedText = resolvedTextByWidgetName.get(field.name);

    return resolvedText
      ? {
          ...field,
          value: resolvedText.value,
          text_connection: resolvedText.text_connection,
        }
      : {
          ...field,
          value: values[field.name] ?? field?.value ?? "",
        };
  });

export const synchronizeFieldValuesByFieldId = (fields = [], values = {}) => {
  const synchronizedValues = { ...values };
  const fieldNamesById = new Map();

  fields.forEach((field) => {
    const fieldId = field?.field_id;
    const fieldName = field?.name;
    if (
      !fieldId ||
      !fieldName ||
      isSpecialRendererFieldId(fieldId) ||
      isGenericFormField(field) ||
      isConnectedCharacterField(field) ||
      isTextConnectedField(field)
    )
      return;

    const fieldNames = fieldNamesById.get(fieldId) || [];
    fieldNames.push(fieldName);
    fieldNamesById.set(fieldId, fieldNames);
  });

  fieldNamesById.forEach((fieldNames) => {
    const sourceName = fieldNames.find((name) => {
      const value = synchronizedValues[name];
      return value !== undefined && value !== null && value !== "";
    });
    if (!sourceName) return;

    const sharedValue = synchronizedValues[sourceName];
    fieldNames.forEach((name) => {
      synchronizedValues[name] = sharedValue;
    });
  });

  return synchronizedValues;
};

export const hasPaisRendererFields = (documentPayload) => {
  if (!documentPayload || typeof documentPayload !== "object") return false;

  const directFields = Array.isArray(documentPayload?.fields)
    ? documentPayload.fields
    : documentPayload?.fields && typeof documentPayload.fields === "object"
      ? Object.values(documentPayload.fields)
      : [];
  const sections = Array.isArray(documentPayload?.sections)
    ? documentPayload.sections
    : documentPayload?.sections && typeof documentPayload.sections === "object"
      ? Object.values(documentPayload.sections)
      : [];
  const sectionFields = sections.flatMap((section) => {
    if (Array.isArray(section?.fields)) return section.fields;
    if (section?.fields && typeof section.fields === "object") {
      return Object.values(section.fields);
    }
    return [];
  });

  return [...directFields, ...sectionFields].some(isPaisRendererField);
};

export const getDocumentLookup = (documents = {}, doc = null) => {
  const key = getDocumentKey(doc);

  return {
    key,
    data: key ? documents?.[key] : undefined,
  };
};

// Helper to check if the current form (by leftValue) is NOC
export const isCurrentFormNOC = (
  selectedRow,
  selectedPermitType,
  leftValue,
) => {
  const permitData = getPermitTypeData(selectedRow, selectedPermitType);

  return permitData?.selected_documents?.[leftValue]?.is_noc === true;
};

// Helper to find the index of NOC in selected_documents
export const getNOCIndex = (selectedRow, selectedPermitType) => {
  const permitData = getPermitTypeData(selectedRow, selectedPermitType);
  const index = permitData?.selected_documents?.findIndex(
    (doc) => doc?.is_noc === true,
  );
  return index >= 0 ? index : -1;
};

// Helper to check if NOC exists in the permit
export const hasNOC = (selectedRow, selectedPermitType) => {
  return getNOCIndex(selectedRow, selectedPermitType) >= 0;
};

// Reusable function to override contractor data for additional permits
export const overrideContractorDataForAdditionalPermit = (
  merged,
  commonFields,
  additionalPermitContractor,
) => {
  if (!additionalPermitContractor) return merged;

  const firstName = additionalPermitContractor?.firstName || "";
  const middleName = additionalPermitContractor?.middleName || "";
  const lastName = additionalPermitContractor?.lastName || "";
  const fullName = [firstName, middleName, lastName]
    .filter(Boolean)
    .map((name) => name.charAt(0).toUpperCase() + name.slice(1).toLowerCase())
    .join(" ");

  const contractorValueMap = {
    contractorName: fullName,
    contractor_name: fullName,
    contractorQualifyName: fullName,
    contractorQualify: fullName,
    contractorSigneeName: fullName,
    contractorBusinessName: additionalPermitContractor?.businessName || "",
    contractorBusiness: additionalPermitContractor?.businessName || "",
    contractorBName: additionalPermitContractor?.businessName || "",
    contractorCompany: additionalPermitContractor?.businessName || "",
    contractorAddress: additionalPermitContractor?.mainAddress || "",
    contractor_address: additionalPermitContractor?.mainAddress || "",
    contractorStreet: additionalPermitContractor?.address1 || "",
    contractorCity: additionalPermitContractor?.city || "",
    contractorState: additionalPermitContractor?.state || "",
    contractorZip: additionalPermitContractor?.zip || "",
    contractorZipCode: additionalPermitContractor?.zip || "",
    contractorLicense: additionalPermitContractor?.licenseNumber || "",
    contractorLicenseNum: additionalPermitContractor?.licenseNumber || "",
    contractorEmail: additionalPermitContractor?.email || "",
    contractor_email: additionalPermitContractor?.email || "",
    contractorPhone: additionalPermitContractor?.mobile || "",
    contractor_phone: additionalPermitContractor?.mobile || "",
    contractorMobile: additionalPermitContractor?.mobile || "",
    contractor_mobile: additionalPermitContractor?.mobile || "",
    contractorFax: additionalPermitContractor?.fax || "",
    contractorCounty: additionalPermitContractor?.mainAddressCounty || "",
    cityID:
      additionalPermitContractor?.contractorCity ||
      additionalPermitContractor?.city ||
      "",
  };

  Object.entries(contractorValueMap).forEach(([fieldName, contractorValue]) => {
    if (
      contractorValue !== undefined &&
      contractorValue !== null &&
      contractorValue !== "" &&
      Object.prototype.hasOwnProperty.call(merged, fieldName)
    ) {
      merged[fieldName] = contractorValue;
    }
  });

  return merged;
};

export // Generate permit number based on type
const getPermitPrefix = (permitType) => {
  const prefixMap = {
    building_permit: "B",
    electrical_permit: "E",
    mechanical_permit: "M",
    plumbing_permit: "P",
    fire_permit: "F",
    roofing_permit: "R",
    sign_permit: "S",
    sitework_permit: "SW",
    mobile_home_permit: "MH",
    right_of_way_permit: "ROW",
  };
  return prefixMap[permitType] || "B";
};

export const generatePermitNumber = (
  permitType,
  associateIndex = 0,
  basePermitNumber = null,
) => {
  const prefix = getPermitPrefix(permitType);

  // Extract base number from main permit if provided
  let baseNumber = "24-109825"; // fallback
  if (basePermitNumber) {
    // Extract the middle part from permit numbers like "B-25-332448.000"
    const match = basePermitNumber.match(/^[A-Z]+-(\d+-\d+)/);
    if (match) {
      baseNumber = match[1];
    }
  }

  const suffix =
    associateIndex === 0 ? "000" : String(associateIndex).padStart(3, "0");
  return `${prefix}-${baseNumber}.${suffix}`;
};

export const getAllPermitsInPackage = (selectedRow, selectedPermitType) => {
  const permits = [];

  // Get the main permit number from various possible sources
  const mainPermitNumber =
    selectedRow?.permit_number ||
    selectedRow?.connect_to_city?.permit?.full_permit_number ||
    selectedRow?.documents?.noc?.fields?.permitNumber ||
    null;

  // Add main permit (always base)
  const mainPermit = {
    id: "main",
    type: selectedRow?.permit_type || "building_permit",
    name:
      selectedRow?.permit_type
        ?.replace("_", " ")
        ?.replace(/\b\w/g, (l) => l.toUpperCase()) || "Building Permit",
    isBase: true,
    isConnectedToCity: selectedRow?.connect_to_city?.is_connected || false,
    permitNumber:
      mainPermitNumber ||
      generatePermitNumber(
        selectedRow?.permit_type || "building_permit",
        0,
        mainPermitNumber,
      ),
    isCurrent: false,
  };

  permits.push(mainPermit);

  // Add additional permits (sorted by creation order, latest first)
  if (selectedRow?.additional_permits) {
    const additionalPermitEntries = Object.entries(
      selectedRow.additional_permits,
    ).sort(([, a], [, b]) => {
      // Sort by creation time if available, otherwise by permit type
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });

    additionalPermitEntries.forEach(([permitType, additionalPermit], index) => {
      // Generate permit number using the main permit as base
      const additionalPermitNumber =
        additionalPermit?.connect_to_city?.permit?.full_permit_number ||
        generatePermitNumber(permitType, index + 1, mainPermitNumber);

      permits.push({
        id: permitType,
        type: permitType,
        name: permitType
          .replace("_", " ")
          .replace(/\b\w/g, (l) => l.toUpperCase()),
        isBase: false,
        isConnectedToCity:
          additionalPermit?.connect_to_city?.is_connected || false,
        permitNumber: additionalPermitNumber,
        isCurrent: permitType === selectedPermitType,
        status: "Associated",
      });
    });
  }

  return permits;
};

// Helper function to get the Proposed Use and Structure Type titles from base_permit_map
export const getPermitStructureInfo = (selectedRow, pipeline_tree = null) => {
  if (!selectedRow?.connect_to_city?.base_permit_map) {
    return {
      proposedUseTitle: null,
      structureTypeTitle: null,
    };
  }

  const baseMap = selectedRow.connect_to_city.base_permit_map;
  const proposedUseId = baseMap[1]; // Second element is ProposedUseId
  const structureTypeId = baseMap[2]; // Third element is StructureTypeId
  const permitTypeId = baseMap[0]; // First element is PermitTypeId

  let proposedUseTitle = null;
  let structureTypeTitle = null;

  try {
    if (pipeline_tree) {
      // Find the permit type
      const permit = pipeline_tree.find((p) => p.id === permitTypeId);
      if (permit && permit.proposed_use) {
        const proposedUse = permit.proposed_use.find(
          (p) => p.id === proposedUseId,
        );
        if (proposedUse) {
          proposedUseTitle = proposedUse.name
            ?.replace(/_/g, " ")
            ?.replace(/\b\w/g, (l) => l.toUpperCase());

          if (proposedUse.structure_type) {
            const structureType = proposedUse.structure_type.find(
              (s) => s.id === structureTypeId,
            );
            if (structureType) {
              structureTypeTitle = structureType.name
                ?.replace(/_/g, " ")
                ?.replace(/\b\w/g, (l) => l.toUpperCase());
            }
          }
        }
      }
    }

    // Fallback to IDs if pipeline_tree not available or lookup fails
    if (!proposedUseTitle)
      proposedUseTitle = `Proposed Use ID: ${proposedUseId}`;
    if (!structureTypeTitle)
      structureTypeTitle = `Structure Type ID: ${structureTypeId}`;
  } catch (error) {
    console.error("Error getting permit structure info:", error);
    proposedUseTitle = `Proposed Use ID: ${proposedUseId}`;
    structureTypeTitle = `Structure Type ID: ${structureTypeId}`;
  }

  return {
    proposedUseTitle,
    structureTypeTitle,
    proposedUseId,
    structureTypeId,
  };
};

/**
 * Sorts field initials by page → y (desc) → x (asc).
 */
export const sortInitials = (initials) =>
  [...initials].sort((a, b) => {
    const pageA = a.page_num ?? a.page ?? 0;
    const pageB = b.page_num ?? b.page ?? 0;
    if (pageA !== pageB) return pageA - pageB;

    const yA = a.y ?? a.rect?.Y ?? 0;
    const yB = b.y ?? b.rect?.Y ?? 0;
    if (yA !== yB) return yB - yA;

    const xA = a.x ?? a.rect?.X ?? 0;
    const xB = b.x ?? b.rect?.X ?? 0;
    return xA - xB;
  });

/**
 * Merges option and type fields under their parent
 */
const mergeOptionFields = (initials, fieldMap) => {
  const mergedMap = new Map();

  initials.forEach((field, index) => {
    // Apply generated labels separately from widget data.
    if (
      isPreviewGroupLabelRecord(
        field,
        fieldMap.get(field?.field_id)?.field_type,
      )
    ) {
      return;
    }
    const formField = fieldMap.get(field.field_id);
    const isGenericTableField =
      String(field?.field_id || "").toLowerCase() === "generic_table" &&
      field?.generic_table?.table_id;
    const isOptionGroup = ["checkboxGroup", "selectGroup"].includes(
      formField?.field_type,
    );
    const isGenericField =
      Boolean(field?.is_generic) ||
      (String(field?.field_id || "")
        .toLowerCase()
        .startsWith("generic_") &&
        !isGenericTableField);
    const connectedTextGroupId = field?.text_connection?.group_id;
    const genericFallbackKey = isGenericTableField
      ? field?.id || field?.name || `generic-table-${index}`
      : field?.id || field?.name || `generic-field-${index}`;
    const fieldKey =
      connectedTextGroupId ||
      (!isOptionGroup && field?.field_instance_id) ||
      (isGenericTableField || isGenericField
        ? genericFallbackKey
        : field?.field_id ||
          field?.id ||
          field?.name ||
          `unassigned-field-${index}`);

    if (formField?.field_type === "option") {
      const parentId = formField.parent_field_id;
      const updatedField = {
        ...field,
        label:
          field?.field_label ??
          field?.label ??
          formField?.field_label ??
          formField?.label,
        value: field.field_id,
      };

      if (!parentId || !fieldMap.has(parentId)) {
        mergedMap.set(fieldKey, field);
      } else if (mergedMap.has(parentId)) {
        const existingParent = mergedMap.get(parentId);
        mergedMap.set(parentId, {
          ...existingParent,
          options: [
            ...(Array.isArray(existingParent?.options)
              ? existingParent.options
              : []),
            updatedField,
          ],
        });
      } else {
        const parentField = fieldMap.get(parentId);
        mergedMap.set(parentId, {
          ...parentField,
          options: [updatedField],
          block_id: field?.block_id ?? field?.section,
          block_label: field?.block_label ?? field?.section,
        });
      }
    } else {
      const existingField = mergedMap.get(fieldKey);
      mergedMap.set(
        fieldKey,
        existingField
          ? {
              ...existingField,
              ...field,
              block_id: field?.block_id || existingField?.block_id,
              block_label: field?.block_label || existingField?.block_label,
              ...(Array.isArray(existingField?.options)
                ? { options: existingField.options }
                : {}),
            }
          : field,
      );
    }
  });

  initials.forEach((override) => {
    if (
      !isPreviewGroupLabelRecord(
        override,
        fieldMap.get(override?.field_id)?.field_type,
      )
    ) {
      return;
    }

    const existing = mergedMap.get(override.field_id);
    const groupType = fieldMap.get(override.field_id)?.field_type;

    if (!existing || !["checkboxGroup", "selectGroup"].includes(groupType)) {
      return;
    }

    const label = override.field_label ?? override.label;
    mergedMap.set(override.field_id, {
      ...existing,
      ...(override.field_settings != null
        ? {
            field_settings: {
              ...getFieldSettings(existing),
              ...getFieldSettings(override),
            },
          }
        : {}),
      ...(typeof label === "string" && label.trim()
        ? { field_label: label, label }
        : {}),
    });
  });

  mergedMap.forEach((field, fieldKey) => {
    const fieldType = fieldMap.get(field?.field_id ?? fieldKey)?.field_type;
    if (
      !["checkboxGroup", "selectGroup"].includes(fieldType) ||
      !Array.isArray(field?.options)
    ) {
      return;
    }

    const hasAnyPersistedOrder = field.options.some(
      (option) =>
        option?.order !== null &&
        option?.order !== undefined &&
        Number.isFinite(Number(option.order)),
    );
    if (!hasAnyPersistedOrder) return;

    mergedMap.set(fieldKey, {
      ...field,
      options: field.options
        .map((option, index) => ({ option, index }))
        .sort((a, b) => {
          const orderA = Number.isFinite(Number(a.option?.order))
            ? Number(a.option.order)
            : Infinity;
          const orderB = Number.isFinite(Number(b.option?.order))
            ? Number(b.option.order)
            : Infinity;
          return orderA !== orderB ? orderA - orderB : a.index - b.index;
        })
        .map(({ option }) => option),
    });
  });

  return mergedMap;
};

export const dedupeFieldsByCanonicalId = (fields = []) => {
  const seen = new Set();

  return fields.filter((field, index) => {
    const key =
      field?.field_id ||
      field?.field_instance_id ||
      field?.id ||
      field?.name ||
      `unassigned-field-${index}`;

    if (seen.has(key)) return false;

    seen.add(key);
    return true;
  });
};

const enrichField = (field, fieldMap, fields) => {
  const formField = isConnectionSourceField(field)
    ? undefined
    : fieldMap.get(field.field_id);
  const fallbackType =
    field?.field_type ??
    (field?.type === "PDFTextField" ? "text" : field?.type);

  if (
    formField?.location === "sign" ||
    field.type === "PDFRectangle" ||
    field.type === "PDFLine"
  ) {
    return null;
  }

  const enriched = {
    ...field,
    label:
      field?.field_label ??
      field?.label ??
      formField?.field_label ??
      formField?.label,
    location: formField?.location ?? field?.location,
    field_type: field.text_connection_source
      ? "multiline"
      : (formField?.field_type ?? fallbackType),
    parent_field_id: formField?.parent_field_id ?? field?.parent_field_id,
    category_id: formField?.category_id ?? field?.category_id,
    category_label: formField?.category_label ?? field?.category_label,
    name: field?.name || formField?.field_name || field?.field_id,
    type: field.text_connection_source
      ? "multiline"
      : (formField?.field_type ?? fallbackType),
    multiple: formField?.multiple ?? field?.multiple,
    required: field?.required ?? formField?.required,
    mark_circle: field?.mark_circle ?? formField?.mark_circle,
    def_value: formField?.def_value ?? field?.def_value,
    subscribers: [],
    getValue: () => {},
  };

  if (formField?.field_type === "checkboxGroup") {
    enriched.title = enriched.label;
    if (!enriched?.options || enriched?.options?.length < 0) {
      enriched.options = fields
        ?.filter((f) => f.parent_field_id === field?.field_id)
        .map((f) => ({ value: f?.field_id, label: f?.field_label }));
    } else {
      enriched.options = enriched?.options?.map((f) => ({
        ...f,
        value: f?.field_id ?? f?.value,
        label:
          f?.field_label ??
          f?.label ??
          fieldMap.get(f?.field_id)?.field_label ??
          fieldMap.get(f?.field_id)?.label,
      }));
    }
  }

  if (formField?.field_type === "selectGroup") {
    if (!enriched?.options || enriched?.options?.length < 0) {
      enriched.options = fields
        ?.filter((f) => f.parent_field_id === field?.field_id)
        .map((f) => ({ value: f?.field_id, label: f?.field_label }));
    } else {
      enriched.options = enriched?.options?.map((f) => ({
        ...f,
        value: f?.field_id ?? f?.value,
        label:
          f?.field_label ??
          f?.label ??
          fieldMap.get(f?.field_id)?.field_label ??
          fieldMap.get(f?.field_id)?.label,
      }));
    }
  }

  return applyFieldSettings(enriched);
};

const buildAllFields = (mergedMap, fieldMap, fields) => {
  const allFields = [];

  for (const field of mergedMap.values()) {
    const enriched = enrichField(field, fieldMap, fields);
    if (enriched) allFields.push(enriched);
  }

  return allFields;
};

const applyPersistedWidgetOrders = (allFields, initials) => {
  const earliestOrderByFieldId = new Map();

  (initials ?? []).forEach((widget) => {
    const isGeneric =
      Boolean(widget?.is_generic) ||
      String(widget?.field_id || "")
        .toLowerCase()
        .startsWith("generic_");
    const key = isGeneric ? widget?.name || widget?.field_id : widget?.field_id;
    const order = Number(widget?.order);
    if (!key || !Number.isFinite(order)) return;

    const currentOrder = earliestOrderByFieldId.get(key);
    if (currentOrder === undefined || order < currentOrder) {
      earliestOrderByFieldId.set(key, order);
    }
  });

  return allFields.map((field) => {
    const isGeneric =
      Boolean(field?.is_generic) ||
      String(field?.field_id || "")
        .toLowerCase()
        .startsWith("generic_");
    const linkedFieldIds = [
      isGeneric ? field?.name || field?.field_id : field?.field_id,
      ...(field?.options ?? []).map((option) => option?.value),
    ].filter(Boolean);
    const linkedOrders = linkedFieldIds
      .map((fieldId) => earliestOrderByFieldId.get(fieldId))
      .filter((order) => order !== undefined);

    if (!linkedOrders.length) return field;

    const persistedOrder = Math.min(...linkedOrders);
    return Number(field?.order) === persistedOrder
      ? field
      : { ...field, order: persistedOrder };
  });
};

const isBlankInitialValue = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

export const mergeBlankValuesFromDefaults = (
  values = {},
  defaults = {},
  { replaceFalseKeys = [] } = {},
) => {
  const mergedValues = { ...values };
  const falseIsBlankFor = new Set(replaceFalseKeys);

  Object.entries(defaults || {}).forEach(([fieldName, defaultValue]) => {
    if (
      (isBlankInitialValue(mergedValues[fieldName]) ||
        (mergedValues[fieldName] === false &&
          falseIsBlankFor.has(fieldName))) &&
      !isBlankInitialValue(defaultValue)
    ) {
      mergedValues[fieldName] = defaultValue;
    }
  });

  return mergedValues;
};

export const normalizeFormGroupValue = (field, value) => {
  if (!["checkboxGroup", "selectGroup"].includes(field?.field_type)) {
    return value;
  }

  const options = field?.options ?? [];

  const normalizeOption = (selected) => {
    if (selected === undefined || selected === null || selected === "") {
      return selected;
    }

    const option =
      options.find((item) => item.value === selected) ||
      options.find((item) =>
        [item.field_id, item.name, item.field_name].some(
          (alias) => alias !== undefined && alias === selected,
        ),
      );

    return option?.value ?? selected;
  };

  if (Array.isArray(value)) {
    const normalized = value.map(normalizeOption);

    return field.field_type === "selectGroup" && field.multiple
      ? normalized
      : (normalized[0] ?? "");
  }

  return normalizeOption(value);
};

export const normalizeFormGroupValues = (values = {}, fields = []) => {
  const normalized = { ...values };

  fields.forEach((field) => {
    if (!Object.prototype.hasOwnProperty.call(normalized, field.name)) {
      return;
    }

    normalized[field.name] = normalizeFormGroupValue(
      field,
      normalized[field.name],
    );
  });

  return normalized;
};

export const resolveFormFieldDefaultValue = (field) => {
  const isGroup = ["checkboxGroup", "selectGroup"].includes(field?.field_type);
  const isUnselectedGroup = isGroup && field?.value === false;

  if (!field || (!isBlankInitialValue(field?.value) && !isUnselectedGroup)) {
    return normalizeFormGroupValue(field, field?.value ?? "");
  }

  const defaultValue = field?.def_value;

  if (!isGroup) {
    return defaultValue ?? "";
  }

  if (!Array.isArray(defaultValue)) {
    return "";
  }

  const availableOptionIds = new Set(
    (field?.options ?? []).map((option) => option?.value).filter(Boolean),
  );
  const matchingDefaultIds = defaultValue.filter((fieldId) =>
    availableOptionIds.has(fieldId),
  );

  if (field?.field_type === "selectGroup" && field?.multiple) {
    return matchingDefaultIds;
  }

  return matchingDefaultIds[0] ?? "";
};

export const getSections = (
  initials,
  fields,
  { applyDefaultValues = false, measureTextConnections = false } = {},
) => {
  const fieldMap = new Map(fields?.map((f) => [f.field_id, f]) ?? []);
  const mergedMap = mergeOptionFields(
    getTextConnectionDisplayFields(
      getConnectedDisplayFields(normalizeGenericFields(initials ?? [])),
      {
        measureText: measureTextConnections,
      },
    ),
    fieldMap,
  );
  const allFields = applyPersistedWidgetOrders(
    buildAllFields(mergedMap, fieldMap, fields).filter(
      (field) => String(field?.field_id ?? "").trim().length > 0,
    ),
    initials,
  );
  const displayFields = allFields.filter((field) => !isCanvasAreaField(field));

  const directFields = [];
  const sectionMap = {};

  for (const field of displayFields) {
    const templateCategory = getTemplateCategoryConfig(field?.category_id);
    if (templateCategory) {
      const categorySection = `template-category:${field.category_id}`;
      (sectionMap[categorySection] ??= []).push(field);
      continue;
    }
    const blockId = field?.block_id?.split("__sub")?.[0] ?? field?.section;
    if (blockId) {
      (sectionMap[blockId] ??= []).push(field);
    } else {
      directFields.push(field);
    }
  }

  const sections = Object.entries(sectionMap).map(([section, fields]) => ({
    section,
    fields,
  }));

  return {
    sections,
    directItems: directFields,
    totalFields:
      sections.reduce((acc, s) => acc + s.fields.length, 0) +
      directFields.length,
    allFields,
    initialValues: allFields.reduce((prev, field) => {
      const key =
        field.field_type === "option" ? field.parent_field_id : field.name;
      const value = applyDefaultValues
        ? resolveFormFieldDefaultValue(field)
        : normalizeFormGroupValue(field, field?.value ?? "");
      return { ...prev, [key]: value };
    }, {}),
  };
};

export const getCombineFields = (initials, fields) => {
  const fieldMap = new Map(fields?.map((f) => [f.field_id, f]) ?? []);
  const mergedMap = mergeOptionFields([...(initials ?? [])], fieldMap);
  return buildAllFields(mergedMap, fieldMap, fields);
};

const getLabel = (doc) => getDocumentLabel(doc)?.toLowerCase?.() || "";

export const isPaisDoc = (doc) => {
  const label = getLabel(doc);
  return (
    label.includes("product approval information sheet") ||
    /\bpais\b/.test(label) ||
    /\bpass\b/.test(label)
  );
};

export const isPaisRendererDocument = (doc, documentPayload) =>
  hasPaisRendererFields(documentPayload) || isPaisDoc(doc);

export const isNocAffidavitDoc = (doc) => {
  const label = getLabel(doc);

  return (
    label.includes("affidavit of notice of commencement") ||
    (/\baffidavit\b/.test(label) && /\bnoc\b/.test(label))
  );
};

export const isScheduleOfValuesDoc = (doc) => {
  const label = getLabel(doc);
  return label.includes("schedule of values") || /\bsov\b/.test(label);
};

export const isPlanComplianceAffidavitDoc = (doc) => {
  const label = getLabel(doc);
  return label.includes("plan compliance affidavit") || /\bpca\b/.test(label);
};

export const isPpntboDoc = (doc) => {
  const label = getLabel(doc);
  return (
    label.includes("notice to building official") ||
    /\bpp_ntbo\b/.test(label) ||
    /\bppntbo\b/.test(label) ||
    /\bntbo\b/.test(label)
  );
};

export const isDamageWorksheetDoc = (doc) => {
  const label = getLabel(doc);
  return label.includes("damage worksheet") || /\bdw\b/.test(label);
};

export const PAIS_COLUMN_DEFINITIONS = [
  { code: "a", label: "Manufacturer" },
  { code: "b", label: "Product Description" },
  { code: "c", label: "Code Version" },
  { code: "d", label: "FL#" },
  { code: "e", label: "Expiration Date" },
];

export const COLUMN_CODE_MAP = PAIS_COLUMN_DEFINITIONS.map(
  (column) => column.code,
);

export const buildPaisFieldTag = (categoryCode, subCategoryCode, columnCode) =>
  `${categoryCode}_${subCategoryCode}_${columnCode}`;

export const handleNumberKeyDown = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return; // allow copy/paste/select-all
  if (e.key.length === 1 && !/[\d.]/.test(e.key)) {
    e.preventDefault();
  }
};

// Reject pasted text that isn't a valid number
export const handleNumberPaste = (e) => {
  const text = e.clipboardData.getData("text");
  if (!/^\d*\.?\d*$/.test(text)) {
    e.preventDefault();
  }
};

export const canRenderFieldCardEntry = (field, fields = []) => {
  const getRow = (entry) =>
    entry?.row ?? entry?.options?.find((option) => option?.row)?.row;

  const row = getRow(field);
  if (!row) return true;

  const [rowId, position] = row.split("_");
  const rowPosition = Number(position);

  if (!Number.isFinite(rowPosition)) return false;

  const positions = fields.flatMap((entry) => {
    const entryRow = getRow(entry);
    if (!entryRow) return [];

    const [entryRowId, entryPosition] = entryRow.split("_");
    const numericPosition = Number(entryPosition);

    return entryRowId === rowId && Number.isFinite(numericPosition)
      ? [numericPosition]
      : [];
  });

  return positions.length > 0 && rowPosition === Math.min(...positions);
};
